import { type Address, type Network, MAINNET, parseAddress } from './address';

export const SATS_PER_COIN = 100_000_000;
export const MAX_MONEY = 10_000_000 * SATS_PER_COIN;

/**
 * Parses "12", "12.5", "12,5", "0.00000001", ".5" into satoshis, exactly
 * (no floating point). Returns null for anything else, more than 8 decimals
 * or above MAX_MONEY.
 */
export function parseAmount(text: string): number | null {
  let s = text.trim().replace(/[   ]/g, '');
  if (s.includes(',') && s.includes('.')) return null;
  s = s.replace(',', '.');
  const parts = s.split('.');
  if (!s || parts.length > 2) return null;
  const whole = parts[0];
  const frac = parts.length === 2 ? parts[1] : '';
  if (!/^[0-9]*$/.test(whole) || !/^[0-9]*$/.test(frac)) return null;
  if (frac.length > 8 || whole.length > 9 || (!whole && !frac)) return null;
  const value = Number(whole || '0') * SATS_PER_COIN + Number(frac.padEnd(8, '0'));
  return value <= MAX_MONEY ? value : null;
}

/** "12.5" style, with "." and no trailing zeros (URIs and text fields). */
export function plainAmount(sats: number): string {
  const v = Math.abs(sats);
  let frac = String(v % SATS_PER_COIN).padStart(8, '0').replace(/0+$/, '');
  const whole = Math.floor(v / SATS_PER_COIN);
  const body = frac ? `${whole}.${frac}` : `${whole}`;
  return sats < 0 ? '-' + body : body;
}

export type PaymentRequestError = 'invalidAddress' | 'invalidAmount' | 'unsupportedRequirement';

export class InvalidPaymentRequest extends Error {
  constructor(public readonly reason: PaymentRequestError) {
    super(reason);
  }
}

export interface PaymentRequest {
  address: Address;
  amount: number | null;
  label: string | null;
}

/**
 * Web link put in COINWOW QR codes: the COINWOW apps read it like a "coinwow:"
 * code, and a phone without the app opens it in app.coinwow.ca.
 */
export const PAYMENT_LINK_PREFIX = 'https://app.coinwow.ca/pay';

/**
 * What the receive QR codes contain. false: "coinwow:cw1q…" (the phone's camera
 * opens the COINWOW app). Switch to true once the iPhone app is on the App Store
 * with Universal Links: the web link then opens the app directly, or this web app
 * for people without it. Same switch in the iPhone app and on the watch.
 */
export const QR_USES_WEB_LINK = false;

/** Content of a receive QR code (see QR_USES_WEB_LINK). */
export function qrContent(address: string, amount: number | null = null): string {
  if (QR_USES_WEB_LINK) return paymentLink(address, amount);
  return `coinwow:${address}` + (amount !== null && amount > 0 ? `?amount=${plainAmount(amount)}` : '');
}

/** "https://app.coinwow.ca/pay?to=cw1q…&amount=5" (web link for receiving). */
export function paymentLink(address: string, amount: number | null = null): string {
  let s = `${PAYMENT_LINK_PREFIX}?to=${address}`;
  if (amount !== null && amount > 0) s += `&amount=${plainAmount(amount)}`;
  return s;
}

/** "https://app.coinwow.ca/pay?to=X&amount=Y" -> "coinwow:X?amount=Y"; anything else unchanged. */
export function normalizePaymentLink(s: string): string {
  let url: URL;
  try {
    url = new URL(s);
  } catch {
    return s;
  }
  if (url.protocol !== 'https:' || url.hostname.toLowerCase() !== 'app.coinwow.ca' || !/^\/pay\/?$/.test(url.pathname)) return s;
  const query = url.search.replace(/^\?/, '');
  const parts = query ? query.split('&') : [];
  const to = parts.find((p) => p.split('=')[0].toLowerCase() === 'to')?.split('=').slice(1).join('=');
  if (!to) return s;
  const rest = parts.filter((p) => p.split('=')[0].toLowerCase() !== 'to');
  return `coinwow:${decodeURIComponent(to)}` + (rest.length ? '?' + rest.join('&') : '');
}

/** "cw1q…", "coinwow:cw1q…?amount=12.5&label=Café" or "https://app.coinwow.ca/pay?to=cw1q…&amount=12.5". */
export function parsePaymentRequest(input: string, network: Network = MAINNET): PaymentRequest {
  const s = normalizePaymentLink(input.trim());
  let address: Address;
  try {
    address = parseAddress(s, network);
  } catch {
    throw new InvalidPaymentRequest('invalidAddress');
  }
  let amount: number | null = null;
  let label: string | null = null;
  const q = s.indexOf('?');
  if (s.toLowerCase().startsWith('coinwow:') && q >= 0) {
    for (const [rawName, value] of new URLSearchParams(s.slice(q + 1))) {
      const name = rawName.toLowerCase();
      if (name === 'amount') {
        // BIP21 amounts always use "." as the decimal separator.
        const a = value.includes(',') ? null : parseAmount(value);
        if (a === null || a <= 0) throw new InvalidPaymentRequest('invalidAmount');
        amount = a;
      } else if (name === 'label') {
        label = value;
      } else if (name.startsWith('req-')) {
        throw new InvalidPaymentRequest('unsupportedRequirement');
      }
    }
  }
  return { address, amount, label };
}

export function tryPaymentRequest(input: string): PaymentRequest | null {
  try {
    return parsePaymentRequest(input);
  } catch {
    return null;
  }
}
